import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  Calendar, Users, Hotel, Gift, Clock, Plus, Trash2, Edit2, Save,
  Sparkles, Download, Upload, UserPlus, BedDouble, CalendarDays,
  Coffee, Utensils, Mic, Users2, MapPin, DollarSign, CheckCircle,
  XCircle, AlertCircle, Lightbulb, ArrowLeft, FileText
} from 'lucide-react';

export default function ItineraryPlannerPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Guest state
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [guestForm, setGuestForm] = useState({
    name: '', email: '', phone: '', category: 'Regular',
    company: '', designation: '', dietary_preference: '', special_needs: '', rsvp_status: 'pending'
  });
  
  // Room state
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [roomForm, setRoomForm] = useState({
    guest_id: '', guest_name: '', room_type: 'Double', check_in: '', check_out: '',
    hotel_name: '', room_number: '', sharing_with: '', cost_per_night: 0, notes: ''
  });
  
  // Schedule state
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    day: 1, date: '', time_start: '', time_end: '', session_type: 'Keynote',
    title: '', description: '', venue: '', speaker: '', notes: ''
  });
  
  // Gift state
  const [showGiftDialog, setShowGiftDialog] = useState(false);
  const [giftForm, setGiftForm] = useState({
    gift_name: '', category: 'Welcome Kit', recipient_category: 'All',
    quantity: 1, unit_cost: 0, total_cost: 0, delivery_date: '', notes: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    if (id) {
      fetchItinerary();
    }
  }, [user, id]);

  const fetchItinerary = async () => {
    try {
      const response = await api.getItinerary(id);
      setItinerary(response.data);
    } catch (error) {
      toast.error('Failed to load itinerary');
      navigate('/itinerary-builder');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await api.getAISuggestions(id);
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    }
  };

  // Guest handlers
  const handleAddGuest = async () => {
    try {
      if (editingGuest) {
        await api.updateGuest(id, editingGuest.id, guestForm);
        toast.success('Guest updated successfully');
      } else {
        await api.addGuest(id, guestForm);
        toast.success('Guest added successfully');
      }
      setShowGuestDialog(false);
      resetGuestForm();
      fetchItinerary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save guest');
    }
  };

  const handleDeleteGuest = async (guestId) => {
    if (!confirm('Are you sure you want to remove this guest?')) return;
    try {
      await api.deleteGuest(id, guestId);
      toast.success('Guest removed');
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to remove guest');
    }
  };

  const resetGuestForm = () => {
    setGuestForm({
      name: '', email: '', phone: '', category: 'Regular',
      company: '', designation: '', dietary_preference: '', special_needs: '', rsvp_status: 'pending'
    });
    setEditingGuest(null);
  };

  const openEditGuest = (guest) => {
    setEditingGuest(guest);
    setGuestForm(guest);
    setShowGuestDialog(true);
  };

  // Room handlers
  const handleAddRoom = async () => {
    try {
      const rooms = [...(itinerary.rooms || []), { ...roomForm, id: Date.now().toString() }];
      await api.updateRooms(id, rooms);
      toast.success('Room allocation added');
      setShowRoomDialog(false);
      setRoomForm({
        guest_id: '', guest_name: '', room_type: 'Double', check_in: '', check_out: '',
        hotel_name: '', room_number: '', sharing_with: '', cost_per_night: 0, notes: ''
      });
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to add room allocation');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    const rooms = itinerary.rooms.filter(r => r.id !== roomId);
    try {
      await api.updateRooms(id, rooms);
      toast.success('Room allocation removed');
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to remove room');
    }
  };

  // Schedule handlers
  const handleAddScheduleItem = async () => {
    try {
      const schedule = [...(itinerary.schedule || []), { ...scheduleForm, id: Date.now().toString() }];
      await api.updateSchedule(id, schedule);
      toast.success('Schedule item added');
      setShowScheduleDialog(false);
      setScheduleForm({
        day: 1, date: '', time_start: '', time_end: '', session_type: 'Keynote',
        title: '', description: '', venue: '', speaker: '', notes: ''
      });
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to add schedule item');
    }
  };

  const handleDeleteScheduleItem = async (itemId) => {
    const schedule = itinerary.schedule.filter(s => s.id !== itemId);
    try {
      await api.updateSchedule(id, schedule);
      toast.success('Schedule item removed');
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to remove schedule item');
    }
  };

  // Gift handlers
  const handleAddGift = async () => {
    try {
      const gifts = [...(itinerary.gifts || []), { ...giftForm, id: Date.now().toString(), total_cost: giftForm.quantity * giftForm.unit_cost }];
      await api.updateGifts(id, gifts);
      toast.success('Gift allocation added');
      setShowGiftDialog(false);
      setGiftForm({
        gift_name: '', category: 'Welcome Kit', recipient_category: 'All',
        quantity: 1, unit_cost: 0, total_cost: 0, delivery_date: '', notes: ''
      });
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to add gift allocation');
    }
  };

  const handleDeleteGift = async (giftId) => {
    const gifts = itinerary.gifts.filter(g => g.id !== giftId);
    try {
      await api.updateGifts(id, gifts);
      toast.success('Gift allocation removed');
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to remove gift');
    }
  };

  // Calculate totals
  const getTotalGuestCount = () => itinerary?.guests?.length || 0;
  const getConfirmedGuests = () => itinerary?.guests?.filter(g => g.rsvp_status === 'confirmed').length || 0;
  const getTotalRoomCost = () => itinerary?.rooms?.reduce((sum, r) => sum + (r.cost_per_night || 0), 0) || 0;
  const getTotalGiftCost = () => itinerary?.gifts?.reduce((sum, g) => sum + (g.quantity * g.unit_cost), 0) || 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-slate-600">Loading itinerary...</p>
        </div>
      </div>
    );
  }

  if (!itinerary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Itinerary not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-navy text-white py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/itinerary-builder')} className="text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Itineraries
          </Button>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{itinerary.event_name}</h1>
              <div className="flex flex-wrap gap-3 text-sm text-slate-300">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(itinerary.start_date).toLocaleDateString()} - {new Date(itinerary.end_date).toLocaleDateString()}</span>
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {itinerary.location}</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {itinerary.attendees} attendees</span>
                <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> ₹{itinerary.budget?.toLocaleString()}</span>
              </div>
            </div>
            <Button onClick={fetchSuggestions} className="bg-gold hover:bg-gold-light text-navy font-bold">
              <Sparkles className="w-4 h-4 mr-2" /> AI Suggestions
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Guests</p>
                  <p className="text-2xl font-bold text-navy">{getTotalGuestCount()}</p>
                  <p className="text-xs text-green-600">{getConfirmedGuests()} confirmed</p>
                </div>
                <Users className="w-10 h-10 text-gold" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Rooms Allocated</p>
                  <p className="text-2xl font-bold text-navy">{itinerary.rooms?.length || 0}</p>
                  <p className="text-xs text-slate-500">₹{getTotalRoomCost().toLocaleString()}</p>
                </div>
                <Hotel className="w-10 h-10 text-gold" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Schedule Items</p>
                  <p className="text-2xl font-bold text-navy">{itinerary.schedule?.length || 0}</p>
                  <p className="text-xs text-slate-500">activities planned</p>
                </div>
                <CalendarDays className="w-10 h-10 text-gold" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Gift Budget</p>
                  <p className="text-2xl font-bold text-navy">₹{getTotalGiftCost().toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{itinerary.gifts?.length || 0} items</p>
                </div>
                <Gift className="w-10 h-10 text-gold" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white shadow-sm mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="guests" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Guests ({getTotalGuestCount()})
            </TabsTrigger>
            <TabsTrigger value="rooms" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              <Hotel className="w-4 h-4 mr-2" /> Rooms ({itinerary.rooms?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              <CalendarDays className="w-4 h-4 mr-2" /> Schedule
            </TabsTrigger>
            <TabsTrigger value="gifts" className="data-[state=active]:bg-navy data-[state=active]:text-white">
              <Gift className="w-4 h-4 mr-2" /> Gifts
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>Event Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-navy mb-2">Event Details</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Type:</strong> {itinerary.event_type}</p>
                        <p><strong>Location:</strong> {itinerary.location}</p>
                        <p><strong>Duration:</strong> {Math.ceil((new Date(itinerary.end_date) - new Date(itinerary.start_date)) / (1000 * 60 * 60 * 24))} days</p>
                        <p><strong>Expected Attendees:</strong> {itinerary.attendees}</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-navy mb-2">Budget Overview</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Total Budget:</strong> ₹{itinerary.budget?.toLocaleString()}</p>
                        <p><strong>Room Cost:</strong> ₹{getTotalRoomCost().toLocaleString()}</p>
                        <p><strong>Gift Cost:</strong> ₹{getTotalGiftCost().toLocaleString()}</p>
                        <p><strong>Remaining:</strong> ₹{(itinerary.budget - getTotalRoomCost() - getTotalGiftCost()).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-navy mb-2">Guest Summary</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Total Guests:</strong> {getTotalGuestCount()}</p>
                        <p className="text-green-600"><strong>Confirmed:</strong> {getConfirmedGuests()}</p>
                        <p className="text-yellow-600"><strong>Pending:</strong> {itinerary.guests?.filter(g => g.rsvp_status === 'pending').length || 0}</p>
                        <p className="text-red-600"><strong>Declined:</strong> {itinerary.guests?.filter(g => g.rsvp_status === 'declined').length || 0}</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gold/10 rounded-lg border border-gold/30">
                      <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-gold" /> Quick Tips
                      </h4>
                      <ul className="text-sm text-slate-700 space-y-1">
                        <li>• Add guests to start planning room allocations</li>
                        <li>• Use AI Suggestions for smart recommendations</li>
                        <li>• Build your day-wise schedule in the Schedule tab</li>
                        <li>• Plan gifts based on guest categories</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guests Tab */}
          <TabsContent value="guests">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Guest Management</CardTitle>
                <Button onClick={() => { resetGuestForm(); setShowGuestDialog(true); }} className="bg-gold hover:bg-gold-light text-navy">
                  <UserPlus className="w-4 h-4 mr-2" /> Add Guest
                </Button>
              </CardHeader>
              <CardContent>
                {itinerary.guests?.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No guests added yet</p>
                    <p className="text-sm">Start by adding your event guests</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-semibold text-navy">Name</th>
                          <th className="text-left p-3 font-semibold text-navy">Email</th>
                          <th className="text-left p-3 font-semibold text-navy">Company</th>
                          <th className="text-left p-3 font-semibold text-navy">Category</th>
                          <th className="text-left p-3 font-semibold text-navy">RSVP</th>
                          <th className="text-left p-3 font-semibold text-navy">Dietary</th>
                          <th className="text-left p-3 font-semibold text-navy">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {itinerary.guests?.map((guest) => (
                          <tr key={guest.id} className="hover:bg-slate-50">
                            <td className="p-3 font-medium text-navy">{guest.name}</td>
                            <td className="p-3 text-slate-600">{guest.email}</td>
                            <td className="p-3 text-slate-600">{guest.company || '-'}</td>
                            <td className="p-3">
                              <Badge variant={guest.category === 'VIP' ? 'default' : 'secondary'} className={guest.category === 'VIP' ? 'bg-gold text-navy' : ''}>
                                {guest.category}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge variant={guest.rsvp_status === 'confirmed' ? 'default' : guest.rsvp_status === 'declined' ? 'destructive' : 'secondary'}
                                className={guest.rsvp_status === 'confirmed' ? 'bg-green-500' : ''}>
                                {guest.rsvp_status}
                              </Badge>
                            </td>
                            <td className="p-3 text-slate-600">{guest.dietary_preference || '-'}</td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => openEditGuest(guest)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteGuest(guest.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rooms Tab */}
          <TabsContent value="rooms">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Room Allocation</CardTitle>
                <Button onClick={() => setShowRoomDialog(true)} className="bg-gold hover:bg-gold-light text-navy">
                  <BedDouble className="w-4 h-4 mr-2" /> Add Room
                </Button>
              </CardHeader>
              <CardContent>
                {itinerary.rooms?.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Hotel className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No rooms allocated yet</p>
                    <p className="text-sm">Add room allocations for your guests</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {itinerary.rooms?.map((room) => (
                      <Card key={room.id} className="border shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-navy">{room.guest_name}</h4>
                              <Badge className="mt-1">{room.room_type}</Badge>
                            </div>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteRoom(room.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-1 text-sm text-slate-600">
                            {room.hotel_name && <p><strong>Hotel:</strong> {room.hotel_name}</p>}
                            {room.room_number && <p><strong>Room:</strong> {room.room_number}</p>}
                            <p><strong>Check-in:</strong> {room.check_in}</p>
                            <p><strong>Check-out:</strong> {room.check_out}</p>
                            <p><strong>Cost/Night:</strong> ₹{room.cost_per_night?.toLocaleString()}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Event Schedule</CardTitle>
                <Button onClick={() => setShowScheduleDialog(true)} className="bg-gold hover:bg-gold-light text-navy">
                  <Plus className="w-4 h-4 mr-2" /> Add Session
                </Button>
              </CardHeader>
              <CardContent>
                {itinerary.schedule?.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CalendarDays className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No schedule items yet</p>
                    <p className="text-sm">Plan your event day by day</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {itinerary.schedule?.sort((a, b) => a.day - b.day || a.time_start?.localeCompare(b.time_start)).map((item) => (
                      <div key={item.id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border-l-4 border-gold">
                        <div className="text-center min-w-[60px]">
                          <div className="text-lg font-bold text-navy">Day {item.day}</div>
                          <div className="text-sm text-slate-500">{item.time_start}</div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-navy">{item.title}</h4>
                            <Badge variant="outline">{item.session_type}</Badge>
                          </div>
                          {item.description && <p className="text-sm text-slate-600 mb-2">{item.description}</p>}
                          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                            {item.time_start && item.time_end && <span><Clock className="w-3 h-3 inline mr-1" /> {item.time_start} - {item.time_end}</span>}
                            {item.venue && <span><MapPin className="w-3 h-3 inline mr-1" /> {item.venue}</span>}
                            {item.speaker && <span><Mic className="w-3 h-3 inline mr-1" /> {item.speaker}</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteScheduleItem(item.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gifts Tab */}
          <TabsContent value="gifts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Gift Allocations</CardTitle>
                <Button onClick={() => setShowGiftDialog(true)} className="bg-gold hover:bg-gold-light text-navy">
                  <Gift className="w-4 h-4 mr-2" /> Add Gift
                </Button>
              </CardHeader>
              <CardContent>
                {itinerary.gifts?.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Gift className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No gifts allocated yet</p>
                    <p className="text-sm">Plan gifts for your guests</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-semibold text-navy">Gift</th>
                          <th className="text-left p-3 font-semibold text-navy">Category</th>
                          <th className="text-left p-3 font-semibold text-navy">For</th>
                          <th className="text-left p-3 font-semibold text-navy">Qty</th>
                          <th className="text-left p-3 font-semibold text-navy">Unit Cost</th>
                          <th className="text-left p-3 font-semibold text-navy">Total</th>
                          <th className="text-left p-3 font-semibold text-navy">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {itinerary.gifts?.map((gift) => (
                          <tr key={gift.id} className="hover:bg-slate-50">
                            <td className="p-3 font-medium text-navy">{gift.gift_name}</td>
                            <td className="p-3"><Badge variant="secondary">{gift.category}</Badge></td>
                            <td className="p-3 text-slate-600">{gift.recipient_category}</td>
                            <td className="p-3 text-slate-600">{gift.quantity}</td>
                            <td className="p-3 text-slate-600">₹{gift.unit_cost?.toLocaleString()}</td>
                            <td className="p-3 font-semibold text-navy">₹{(gift.quantity * gift.unit_cost).toLocaleString()}</td>
                            <td className="p-3">
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteGift(gift.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-navy text-white">
                        <tr>
                          <td colSpan="5" className="p-3 font-bold">Total Gift Budget</td>
                          <td className="p-3 font-bold">₹{getTotalGiftCost().toLocaleString()}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Guest Dialog */}
      <Dialog open={showGuestDialog} onOpenChange={setShowGuestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingGuest ? 'Edit Guest' : 'Add New Guest'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={guestForm.name} onChange={(e) => setGuestForm({...guestForm, name: e.target.value})} />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={guestForm.email} onChange={(e) => setGuestForm({...guestForm, email: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={guestForm.phone} onChange={(e) => setGuestForm({...guestForm, phone: e.target.value})} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={guestForm.category} onValueChange={(v) => setGuestForm({...guestForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VIP">VIP</SelectItem>
                    <SelectItem value="Regular">Regular</SelectItem>
                    <SelectItem value="Speaker">Speaker</SelectItem>
                    <SelectItem value="Organizer">Organizer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Input value={guestForm.company} onChange={(e) => setGuestForm({...guestForm, company: e.target.value})} />
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={guestForm.designation} onChange={(e) => setGuestForm({...guestForm, designation: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dietary Preference</Label>
                <Select value={guestForm.dietary_preference} onValueChange={(v) => setGuestForm({...guestForm, dietary_preference: v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Veg">Vegetarian</SelectItem>
                    <SelectItem value="Non-Veg">Non-Vegetarian</SelectItem>
                    <SelectItem value="Vegan">Vegan</SelectItem>
                    <SelectItem value="Jain">Jain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>RSVP Status</Label>
                <Select value={guestForm.rsvp_status} onValueChange={(v) => setGuestForm({...guestForm, rsvp_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Special Needs</Label>
              <Textarea value={guestForm.special_needs} onChange={(e) => setGuestForm({...guestForm, special_needs: e.target.value})} placeholder="Wheelchair access, allergies, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGuestDialog(false)}>Cancel</Button>
            <Button onClick={handleAddGuest} className="bg-gold text-navy">{editingGuest ? 'Update' : 'Add'} Guest</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={showRoomDialog} onOpenChange={setShowRoomDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Room Allocation</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Guest Name *</Label>
              <Select value={roomForm.guest_name} onValueChange={(v) => {
                const guest = itinerary.guests?.find(g => g.name === v);
                setRoomForm({...roomForm, guest_name: v, guest_id: guest?.id || ''});
              }}>
                <SelectTrigger><SelectValue placeholder="Select guest..." /></SelectTrigger>
                <SelectContent>
                  {itinerary.guests?.map(g => (
                    <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room Type</Label>
                <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({...roomForm, room_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Double">Double</SelectItem>
                    <SelectItem value="Suite">Suite</SelectItem>
                    <SelectItem value="Deluxe">Deluxe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cost per Night</Label>
                <Input type="number" value={roomForm.cost_per_night} onChange={(e) => setRoomForm({...roomForm, cost_per_night: parseFloat(e.target.value)})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check-in</Label>
                <Input type="date" value={roomForm.check_in} onChange={(e) => setRoomForm({...roomForm, check_in: e.target.value})} />
              </div>
              <div>
                <Label>Check-out</Label>
                <Input type="date" value={roomForm.check_out} onChange={(e) => setRoomForm({...roomForm, check_out: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hotel Name</Label>
                <Input value={roomForm.hotel_name} onChange={(e) => setRoomForm({...roomForm, hotel_name: e.target.value})} />
              </div>
              <div>
                <Label>Room Number</Label>
                <Input value={roomForm.room_number} onChange={(e) => setRoomForm({...roomForm, room_number: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoomDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRoom} className="bg-gold text-navy">Add Room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Schedule Item</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day Number *</Label>
                <Input type="number" min="1" value={scheduleForm.day} onChange={(e) => setScheduleForm({...scheduleForm, day: parseInt(e.target.value)})} />
              </div>
              <div>
                <Label>Session Type</Label>
                <Select value={scheduleForm.session_type} onValueChange={(v) => setScheduleForm({...scheduleForm, session_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Keynote">Keynote</SelectItem>
                    <SelectItem value="Breakout">Breakout Session</SelectItem>
                    <SelectItem value="Networking">Networking</SelectItem>
                    <SelectItem value="Meal">Meal Break</SelectItem>
                    <SelectItem value="Activity">Activity</SelectItem>
                    <SelectItem value="Travel">Travel</SelectItem>
                    <SelectItem value="Registration">Registration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={scheduleForm.title} onChange={(e) => setScheduleForm({...scheduleForm, title: e.target.value})} placeholder="e.g., Welcome Keynote" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={scheduleForm.time_start} onChange={(e) => setScheduleForm({...scheduleForm, time_start: e.target.value})} />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={scheduleForm.time_end} onChange={(e) => setScheduleForm({...scheduleForm, time_end: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Venue</Label>
                <Input value={scheduleForm.venue} onChange={(e) => setScheduleForm({...scheduleForm, venue: e.target.value})} placeholder="e.g., Grand Ballroom" />
              </div>
              <div>
                <Label>Speaker</Label>
                <Input value={scheduleForm.speaker} onChange={(e) => setScheduleForm({...scheduleForm, speaker: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={scheduleForm.description} onChange={(e) => setScheduleForm({...scheduleForm, description: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={handleAddScheduleItem} className="bg-gold text-navy">Add Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gift Dialog */}
      <Dialog open={showGiftDialog} onOpenChange={setShowGiftDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Gift Allocation</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Gift Name *</Label>
              <Input value={giftForm.gift_name} onChange={(e) => setGiftForm({...giftForm, gift_name: e.target.value})} placeholder="e.g., Welcome Kit, Executive Pen Set" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gift Category</Label>
                <Select value={giftForm.category} onValueChange={(v) => setGiftForm({...giftForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Welcome Kit">Welcome Kit</SelectItem>
                    <SelectItem value="Speaker Gift">Speaker Gift</SelectItem>
                    <SelectItem value="Attendee Gift">Attendee Gift</SelectItem>
                    <SelectItem value="VIP Gift">VIP Gift</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Recipient Category</Label>
                <Select value={giftForm.recipient_category} onValueChange={(v) => setGiftForm({...giftForm, recipient_category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Guests</SelectItem>
                    <SelectItem value="VIP">VIP Only</SelectItem>
                    <SelectItem value="Speakers">Speakers Only</SelectItem>
                    <SelectItem value="Organizers">Organizers Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input type="number" min="1" value={giftForm.quantity} onChange={(e) => setGiftForm({...giftForm, quantity: parseInt(e.target.value)})} />
              </div>
              <div>
                <Label>Unit Cost (₹)</Label>
                <Input type="number" min="0" value={giftForm.unit_cost} onChange={(e) => setGiftForm({...giftForm, unit_cost: parseFloat(e.target.value)})} />
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <span className="text-sm text-slate-600">Total Cost: </span>
              <span className="text-lg font-bold text-navy">₹{(giftForm.quantity * giftForm.unit_cost).toLocaleString()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGiftDialog(false)}>Cancel</Button>
            <Button onClick={handleAddGift} className="bg-gold text-navy">Add Gift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Suggestions Dialog */}
      <Dialog open={showSuggestions} onOpenChange={setShowSuggestions}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" /> AI Suggestions for Your Event
            </DialogTitle>
          </DialogHeader>
          {suggestions && (
            <div className="space-y-6 py-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Hotel className="w-4 h-4" /> Room Recommendations
                </h4>
                <p className="text-sm text-blue-700">{suggestions.rooms.tip}</p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                  <div className="p-2 bg-white rounded"><strong>Single:</strong> {suggestions.rooms.single_rooms}</div>
                  <div className="p-2 bg-white rounded"><strong>Double:</strong> {suggestions.rooms.double_rooms}</div>
                  <div className="p-2 bg-white rounded"><strong>Suite:</strong> {suggestions.rooms.suite_rooms}</div>
                </div>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <Utensils className="w-4 h-4" /> Food & Beverage
                </h4>
                <p className="text-sm text-green-700">{suggestions.meals.tip}</p>
                <p className="text-sm mt-2"><strong>Estimated F&B Budget:</strong> ₹{suggestions.meals.estimated_f_and_b_budget?.toLocaleString()}</p>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4" /> Gift Recommendations
                </h4>
                <p className="text-sm text-amber-700">{suggestions.gifts.tip}</p>
                <div className="mt-2 text-sm">
                  <p><strong>Welcome Kits:</strong> {suggestions.gifts.welcome_kits}</p>
                  <p><strong>VIP Gifts:</strong> {suggestions.gifts.vip_gifts}</p>
                </div>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Budget Allocation
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-purple-700">
                  {Object.entries(suggestions.overall.budget_allocation).map(([key, value]) => (
                    <div key={key} className="p-2 bg-white rounded capitalize">
                      <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                    </div>
                  ))}
                </div>
                <p className="text-sm mt-3 text-purple-800">{suggestions.overall.tip}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowSuggestions(false)} className="bg-navy text-white">Got it!</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
